// --- notification websocket hook ---
import { useEffect, useRef } from "react"
import { useQueryClient } from "@tanstack/react-query"

type NotificationEvent = {
  type: "notification:new"
  userId: string
}

// connect to the notification ws and refetch on new notification
export function useNotificationSocket(enabled: boolean) {
  const queryClient = useQueryClient()
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMounted = useRef(true)

  useEffect(() => {
    if (!enabled) return
    isMounted.current = true

    function connect() {
      if (!isMounted.current) return

      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:"
      const ws = new WebSocket(`${protocol}//${window.location.host}/api/ws/notifications`)
      wsRef.current = ws

      ws.onmessage = (evt) => {
        try {
          const event: NotificationEvent = JSON.parse(evt.data)
          if (event.type === "notification:new") {
            queryClient.invalidateQueries({ queryKey: ["notifications"] })
          }
        } catch {
          // Ignore malformed messages
        }
      }

      ws.onclose = () => {
        if (!isMounted.current) return
        reconnectTimer.current = setTimeout(connect, 3000)
      }

      ws.onerror = () => {
        ws.close()
      }
    }

    connect()

    return () => {
      isMounted.current = false
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current)
      if (wsRef.current) {
        wsRef.current.onclose = null
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [enabled, queryClient])
}
